"use client";

import { FormEvent, useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useSession } from "@/lib/auth/client";
import EntryShell from "@/components/drop/EntryShell";

export default function NotificationSignup({
  dropId,
  productId,
  category,
  sizes,
}: {
  dropId?: number;
  productId?: number;
  category?: string;
  sizes?: string[];
}) {
  const { data: session } = useSession();
  const authedEmail = session?.user?.email ?? "";
  const isAuthed = Boolean(authedEmail);
  const [email, setEmail] = useState("");
  const [size, setSize] = useState("");
  const targetId = dropId ?? productId ?? 0;
  const [pending, setPending] = useState(false);
  const kind = dropId ? "drop_release" : "restock";

  useEffect(() => {
    if (authedEmail && !email) setEmail(authedEmail);
  }, [authedEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    try {
      const effectiveEmail = isAuthed && email !== "" ? authedEmail : email;
      const response = await fetch("/api/notifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: effectiveEmail || undefined,
          dropId,
          productId,
          category,
          size: productId && sizes && sizes.length > 0 ? size || undefined : undefined,
          kind,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Could not save notification request.");
      toast.success(body.message);
      if (!isAuthed) setEmail("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save notification request.");
    } finally {
      setPending(false);
    }
  }

  const shellMode = kind === "restock" ? "restock" as const : "notify" as const;
  const showSize = Boolean(productId) && Boolean(sizes) && sizes!.length > 0;

  return (
    <EntryShell mode={shellMode} state="entry">
      <form
        id={kind === "drop_release" ? "notify" : undefined}
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        <div>
          <h3 className="text-sm font-black uppercase tracking-tight">
            {kind === "restock" ? "Get restock email" : "Get launch email"}
          </h3>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            {kind === "restock" ? "One email when this item is back." : "One email when this drop opens."}
          </p>
        </div>
        {showSize && (
          <select
            value={size}
            onChange={(event) => setSize(event.target.value)}
            className="border-foreground/15 bg-background h-11 rounded-none font-mono text-sm"
            aria-label="Notify size"
          >
            <option value="">Any size</option>
            {sizes!.map((s) => (
              <option key={s} value={s}>
                Size {s}
              </option>
            ))}
          </select>
        )}
        {!isAuthed || email === "" ? (
          <Input
            id={`${kind}-notify-${targetId}`}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required={!isAuthed}
            autoComplete="email"
            className="border-foreground/15 bg-background h-11 rounded-none font-mono text-sm"
          />
        ) : (
          <div className="border-foreground/15 bg-foreground/[0.03] flex items-center justify-between gap-2 border px-3 py-2.5">
            <span className="font-mono text-xs">
              <span className="text-muted-foreground">Signed in ·</span> <span className="font-bold">{authedEmail}</span>
            </span>
            <button
              type="button"
              onClick={() => setEmail("")}
              className="text-muted-foreground hover:text-foreground text-[10px] font-bold tracking-widest uppercase underline decoration-dotted underline-offset-4"
            >
              Change
            </button>
          </div>
        )}
        <Button
          type="submit"
          disabled={pending || (!isAuthed && !email)}
          className="h-11 rounded-none text-xs font-black tracking-[0.14em] uppercase"
        >
          <Bell data-icon="inline-start" />
          {pending ? "Saving…" : kind === "restock" ? "Alert me" : isAuthed ? "Notify — 1 click" : "Notify me"}
        </Button>
      </form>
    </EntryShell>
  );
}
